import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  DomLayoutType,
  GridApi,
  GridReadyEvent,
} from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import cloneDeep from 'lodash-es/cloneDeep';
import differenceWith from 'lodash-es/differenceWith';
import isEqual from 'lodash-es/isEqual';

import { VISIT_COST_OPTION } from '@features/patient-group-dropdown/constants/visit-cost-option.const';

import {
  injectBatchCreatePatientInvoiceables,
  injectBatchUpdatePatientInvoiceables,
  injectPatientInvoiceables,
  refetchPatientInvoiceables,
} from '@models/patient-protocol/api/patient-invoiceables.api';
import { injectProtocolDiscontinuationRate } from '@models/patient-protocol/api/patient-protocol-discontinuation-rate.api';
import {
  injectBatchRemovePatientProtocols,
  injectPatientProtocolSubTypes,
} from '@models/patient-protocol/api/patient-protocol.api';
import {
  injectBatchCreatePatientVisits,
  injectBatchUpdatePatientVisits,
  injectPatientVisits,
  refetchPatientVisits,
} from '@models/patient-protocol/api/patient-visits.api';
import { SiteCostType } from '@models/sites/site-cost.enum';
import { injectTrialSettingByName } from '@models/trial-settings/api/trial-settings.api';
import { DiscontinuationRateMethodology } from '@models/trial-settings/enums/discontinuation-setting.enum';

import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import {
  CreatePatientInvoiceableInput,
  listPatientInvoiceablesQuery,
  listPatientVisitsQuery,
  TrialSettingKey,
  UpdatePatientInvoiceableInput,
} from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { decimalMultiply } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { protocolEntryConfig } from './grid-config/protocol-entry-config';
import {
  FIELDS_TO_VALIDATE,
  ProtocolValidationService,
  ValidationField,
} from './services/protocol-validation.service';
import {
  ProtocolEntryGridRow,
  ProtocolEntryGridRowWithOriginalIndex,
} from './types/protocol-entry-grid-row';
import { calculateCumulativeValues } from './utils/discontinuation-rate.utils';
import {
  getCreatePatientVisitParams,
  getUpdatePatientVisitParams,
} from './utils/update-create-entity.utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular, StickyGridDirective, TableSkeletonComponent],
  providers: [ProtocolValidationService],
  selector: 'aux-protocol-entry-table',
  templateUrl: './protocol-entry-table.component.html',
})
export class ProtocolEntryTableComponent {
  overlayService = inject(OverlayService);
  protocolValidationService = inject(ProtocolValidationService);

  batchCreatePatientInvoiceables = injectBatchCreatePatientInvoiceables();
  batchCreatePatientVisits = injectBatchCreatePatientVisits();
  batchUpdatePatientInvoiceables = injectBatchUpdatePatientInvoiceables();
  batchUpdatePatientVisits = injectBatchUpdatePatientVisits();
  discontinuationRateSetting = injectTrialSettingByName(
    TrialSettingKey.TRIAL_SETTING_DISCONTINUATION_RATE_METHODOLOGY
  );
  flags = injectFlags({
    investigatorDiscontinuationRate: 'investigator_discontinuation_rate',
  });

  protocolVersionId = input.required<string>();
  selectedPatientGroup = input.required<null | string>();

  overallDiscontinuationRate = injectProtocolDiscontinuationRate({
    patientGroup: this.selectedPatientGroup,
    protocolVersionId: this.protocolVersionId,
  });

  selectedCostType = input.required<SiteCostType>();

  patientInvoiceables = injectPatientInvoiceables({
    enabled: computed(() => this.selectedCostType() === SiteCostType.PATIENT_INVOICEABLES),
    protocolVersionId: this.protocolVersionId,
  });
  patientProtocolSubTypes = injectPatientProtocolSubTypes();
  patientVisits = injectPatientVisits({
    enabled: computed(() => this.selectedCostType() === SiteCostType.PATIENT_VISITS),
    protocolVersionId: this.protocolVersionId,
  });
  removePatientProtocols = injectBatchRemovePatientProtocols(true);

  addedRows = signal<ProtocolEntryGridRow[]>([]);
  deleteButtonDisabled = signal(false);
  deletedRows = signal<string[]>([]);
  gridAPI = signal<GridApi<ProtocolEntryGridRow> | null>(null);
  gridData = signal<ProtocolEntryGridRow[]>([]);
  isSaving = signal(false);
  totalItems = signal(0);
  updatedRowIds = signal<string[]>([]);

  hasOverallDiscontinuationRate = computed(
    () =>
      this.discontinuationRateSetting.setting()?.setting_value ===
      DiscontinuationRateMethodology.PROTOCOL_LEVEL
  );

  editMode = input.required<boolean>();

  gridOptions = computed(() => {
    const editMode = this.editMode();
    const selectedCostType = this.selectedCostType();
    const patientProtocolSubTypes = this.patientProtocolSubTypes.data() || [];
    const hasOverallDiscontinuationRate = this.hasOverallDiscontinuationRate();
    const flags = this.flags();

    return untracked(() => {
      return protocolEntryConfig({
        deleteDisabled: this.deleteButtonDisabled,
        editMode,
        flags,
        hasOverallDiscontinuationRate,
        onChanges: (row, column) => this.onChanges(row, column),
        overlayService: this.overlayService,
        patientProtocolSubTypes,
        removeRow: (row) => this.removeRow(row),
        selectedCostType,
      });
    });
  });
  hasChanges = computed(() => {
    return (
      this.addedRows().length > 0 ||
      this.deletedRows().length > 0 ||
      this.updatedRowIds().length > 0
    );
  });
  isLoading = computed(() => {
    return (
      this.patientInvoiceables.isLoading() || this.patientVisits.isLoading() || this.isSaving()
    );
  });
  tableHeightInEditMode = computed(() => {
    if (!this.totalItems()) {
      return '200px';
    }

    if (this.totalItems() > this.maxVisibleRows) {
      return '500px';
    }

    const rowHeight = TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS.rowHeight;
    const additionalGridHeight = 10;

    const tableHeight =
      rowHeight * this.totalItems() + TableConstants.HEADER_HEIGHT + additionalGridHeight;

    return tableHeight + 'px';
  });

  _syncGridOptions = effect(() => {
    // any changes to the grid options will trigger a re-render
    const newGridOptions = this.gridOptions();

    // don't track the gridAPI
    const gridAPI = untracked(() => this.gridAPI());

    if (!gridAPI) {
      return;
    }

    Object.entries(newGridOptions).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gridAPI.setGridOption(key as any, value);
    });
  });
  colsAfterChangeRecalculateDiscontinuation: (keyof ProtocolEntryGridRow)[] = [
    'discontinuation_rate',
    'target_date_days_out',
  ];
  domLayout: DomLayoutType = 'autoHeight';
  initialData: ProtocolEntryGridRow[] = [];
  readonly maxVisibleRows = 14;
  protocolsByCostTypeEffect = effect(() => {
    const selectedCostType = this.selectedCostType();
    const selectedPatientGroup = this.selectedPatientGroup();

    const patientVisitsData = (this.patientVisits.data() || []).filter(({ patient_group_id }) => {
      return selectedPatientGroup === VISIT_COST_OPTION.value
        ? patient_group_id === null
        : patient_group_id === selectedPatientGroup;
    });
    const patientInvoiceablesData = this.patientInvoiceables.data();

    const mapper = new Map<SiteCostType, (listPatientInvoiceablesQuery | listPatientVisitsQuery)[]>(
      [
        [SiteCostType.PATIENT_INVOICEABLES, patientInvoiceablesData || []],
        [SiteCostType.PATIENT_VISITS, patientVisitsData || []],
      ]
    );

    const protocols = mapper.get(selectedCostType) || [];

    this.gridData.set(this.parseProtocols(protocols));

    this.totalItems.set(protocols.length);

    this.calculateCumulativeValues(this.gridData(), (rows) => this.setInitialData(rows));
  });
  refetchPatientInvoiceables = refetchPatientInvoiceables();
  refetchPatientVisits = refetchPatientVisits();

  addRow() {
    const newRow: ProtocolEntryGridRow = {
      discontinuation_rate: null,
      patient_protocol_id: '',
      patient_protocol_name: '',
      patient_protocol_sub_type: null,
      randomID: Utils.uuid(),
    };

    this.gridAPI()?.applyTransactionAsync(
      {
        add: [newRow],
      },
      () => {
        this.totalItems.update((val) => val + 1);
        this.addedRows.update((values) => [...values, newRow]);
      }
    );
  }

  private calculateCumulativeValues(
    rows?: ProtocolEntryGridRow[],
    cb?: (data: ProtocolEntryGridRow[]) => void
  ): void {
    const gridData = rows ?? this.getAllGridData();
    const updatedGridData = calculateCumulativeValues(
      gridData,
      this.hasOverallDiscontinuationRate()
        ? this.overallDiscontinuationRate()?.data?.discontinuation_rate || 0
        : undefined
    );

    this.gridAPI()?.applyTransactionAsync({ update: updatedGridData }, () => {
      if (cb) {
        const updatedGridData = this.getAllGridData();

        cb(updatedGridData);
      }
    });
  }

  cancelChanges(): void {
    this.gridAPI()?.setGridOption('rowData', cloneDeep(this.initialData));
    this.totalItems.set(this.initialData.length);
    this.addedRows.set([]);
    this.deletedRows.set([]);
    this.updatedRowIds.set([]);
    this.domLayout = 'autoHeight';
    this.gridAPI()?.stopEditing();
    this.gridAPI()?.redrawRows();
    setTimeout(() => this.gridAPI()?.sizeColumnsToFit(), 0);
  }

  cellValueChanged(event: CellValueChangedEvent<ProtocolEntryGridRow>): void {
    if (event.source === 'rangeService') {
      this.validateGridData();
    }

    this.updatedRowIds.update((values) => [...values, event.data?.patient_protocol_id || '']);
  }

  constructor() {
    effect(() => {
      this.setInitialData();
    });
  }

  getAllGridData(): ProtocolEntryGridRow[] {
    const gridData: ProtocolEntryGridRow[] = [];

    this.gridAPI()?.forEachNode((node) => {
      if (node.data) {
        gridData.push(node.data);
      }
    });

    return gridData;
  }

  gridSizeChanged(): void {
    if (this.totalItems() <= this.maxVisibleRows) {
      setTimeout(() => this.gridAPI()?.sizeColumnsToFit(), 200);
    }
  }

  private isPatientVisit(
    protocol: listPatientInvoiceablesQuery | listPatientVisitsQuery
  ): protocol is listPatientVisitsQuery {
    return 'optional' in protocol;
  }

  onCellEditingStopped(event: CellEditingStoppedEvent<ProtocolEntryGridRow>): void {
    const colId = event.column.getColId();

    if (FIELDS_TO_VALIDATE.includes(colId as ValidationField)) {
      this.validateSpecificField(colId as ValidationField, event.rowIndex ?? undefined);
    }

    if (event.newValue !== event.oldValue) {
      this.updatedRowIds.update((values) => [...values, event.data?.patient_protocol_id || '']);

      if (
        this.colsAfterChangeRecalculateDiscontinuation.includes(colId as keyof ProtocolEntryGridRow)
      ) {
        this.calculateCumulativeValues();
      }
    }
  }

  onCellValueChanged(event: CellValueChangedEvent<ProtocolEntryGridRow>): void {
    const colId = event.column.getColId();

    if (event.newValue !== event.oldValue) {
      this.updatedRowIds.update((values) => [...values, event.data?.patient_protocol_id || '']);

      if (
        this.colsAfterChangeRecalculateDiscontinuation.includes(colId as keyof ProtocolEntryGridRow)
      ) {
        this.calculateCumulativeValues();
      }
    }
  }

  onChanges(row: ProtocolEntryGridRow, column: keyof ProtocolEntryGridRow) {
    this.updatedRowIds.update((values) => [...values, row.patient_protocol_id]);

    if (column === 'optional') {
      this.calculateCumulativeValues();
    }
  }

  onGridReady(event: GridReadyEvent<ProtocolEntryGridRow>) {
    this.gridAPI.set(event.api);
    this.gridAPI()?.sizeColumnsToFit();
  }

  parseProtocols(
    protocols: (listPatientInvoiceablesQuery | listPatientVisitsQuery)[]
  ): ProtocolEntryGridRow[] {
    return protocols.map((protocol) => {
      let patientVisitsFields = {};

      if (this.isPatientVisit(protocol)) {
        patientVisitsFields = {
          discontinuation_rate: decimalMultiply(protocol.discontinuation_rate || 0.0, 100),
          optional: protocol.optional,
          patient_group_id: protocol.patient_group_id,
          target_date_days_out: protocol.target_date_days_out,
          target_tolerance_days_out: protocol.target_tolerance_days_out,
        };
      }

      return {
        ...patientVisitsFields,
        patient_protocol_id: protocol.id,
        patient_protocol_name: protocol.name,
        patient_protocol_sub_type: protocol.patient_protocol_sub_type,
      };
    });
  }

  removeRow(row: ProtocolEntryGridRow) {
    this.gridAPI()?.applyTransactionAsync(
      {
        remove: [row],
      },
      () => {
        this.totalItems.update((val) => val - 1);

        // check if patient protocol exists
        if (!row.randomID) {
          this.deletedRows.update((values) => [...values, row.patient_protocol_id]);
        }
      }
    );
  }

  saveAll = async (successCb: VoidFunction): Promise<void> => {
    if (!this.validateGridData()) {
      return;
    }

    this.calculateCumulativeValues();

    this.isSaving.set(true);

    const initialData = this.initialData
      .filter((row) => !this.deletedRows().includes(row.patient_protocol_id))
      .map((item, index) => ({ ...item, originalIndex: index + 1 }));

    const currentData = this.getAllGridData()
      .map(({ errors: _, ...item }, index) => ({ ...item, originalIndex: index + 1 }))
      .filter((row) => !!row.randomID || !this.deletedRows().includes(row.patient_protocol_id));

    const diff = differenceWith(currentData, initialData, isEqual);

    const addedRows = diff.filter((row) => !!row.randomID);
    const rowsToUpdate = diff.filter((row) => !row.randomID);

    const mapSaveFn = new Map<SiteCostType, () => Promise<unknown>>([
      [
        SiteCostType.PATIENT_INVOICEABLES,
        () => this.saveAllPatientInvoiceables(addedRows, rowsToUpdate),
      ],
      [SiteCostType.PATIENT_VISITS, () => this.saveAllPatientVisits(addedRows, rowsToUpdate)],
    ]);

    const refetchFnMap = new Map<SiteCostType, () => void>([
      [SiteCostType.PATIENT_INVOICEABLES, () => this.refetchPatientInvoiceables()],
      [SiteCostType.PATIENT_VISITS, () => this.refetchPatientVisits()],
    ]);

    const saveFn = mapSaveFn.get(this.selectedCostType());

    try {
      if (saveFn) {
        await saveFn();

        if (this.deletedRows().length) {
          await this.removePatientProtocols.mutateAsync({ patientProtocolIds: this.deletedRows() });
        }

        const refetchFn = refetchFnMap.get(this.selectedCostType());

        if (refetchFn) {
          refetchFn();
        }
      }

      successCb();

      this.setInitialData(this.getAllGridData());
      this.cancelChanges();

      this.overlayService.success('Successfully saved!');
    } catch (error) {
      if (error instanceof Error) {
        this.overlayService.error(error.message);
      }
    } finally {
      this.isSaving.set(false);
    }
  };

  saveAllPatientInvoiceables(
    addedRows: ProtocolEntryGridRowWithOriginalIndex[],
    rowsToUpdate: ProtocolEntryGridRowWithOriginalIndex[]
  ) {
    const promises: Promise<unknown>[] = [];

    if (addedRows.length) {
      const createInputs: CreatePatientInvoiceableInput[] = addedRows.map((row) => {
        return {
          order_by: row.originalIndex,
          patient_protocol: {
            name: row.patient_protocol_name,
            patient_protocol_sub_type_id: row.patient_protocol_sub_type?.id || '',
            patient_protocol_version_id: this.protocolVersionId(),
          },
          patient_protocol_type: row.patient_protocol_sub_type!.patient_protocol_type,
        };
      });

      promises.push(this.batchCreatePatientInvoiceables.mutateAsync(createInputs));
    }

    if (rowsToUpdate.length) {
      const updateInputs: UpdatePatientInvoiceableInput[] = rowsToUpdate.map((row) => {
        return {
          order_by: row.originalIndex,
          patient_protocol: {
            id: row.patient_protocol_id,
            name: row.patient_protocol_name,
            patient_protocol_sub_type_id: row.patient_protocol_sub_type?.id || '',
            patient_protocol_version_id: this.protocolVersionId(),
          },
          patient_protocol_type: row.patient_protocol_sub_type!.patient_protocol_type,
        };
      });

      promises.push(this.batchUpdatePatientInvoiceables.mutateAsync(updateInputs));
    }

    return Promise.all(promises);
  }

  saveAllPatientVisits(
    addedRows: ProtocolEntryGridRowWithOriginalIndex[],
    rowsToUpdate: ProtocolEntryGridRowWithOriginalIndex[]
  ) {
    const promises: Promise<unknown>[] = [];

    if (addedRows.length) {
      const patientGroup =
        this.selectedPatientGroup() !== VISIT_COST_OPTION.value
          ? this.selectedPatientGroup()
          : null;

      const createInputs = getCreatePatientVisitParams(
        addedRows,
        this.protocolVersionId(),
        patientGroup
      );

      promises.push(this.batchCreatePatientVisits.mutateAsync(createInputs));
    }

    if (rowsToUpdate.length) {
      const updateInputs = getUpdatePatientVisitParams(
        rowsToUpdate,
        this.protocolVersionId(),
        this.hasOverallDiscontinuationRate(),
        this.flags().investigatorDiscontinuationRate
      );

      promises.push(this.batchUpdatePatientVisits.mutateAsync(updateInputs));
    }

    return Promise.all(promises);
  }

  setInitialData(gridData?: ProtocolEntryGridRow[]) {
    this.initialData = cloneDeep(gridData || this.gridData().map((row) => ({ ...row })));
  }

  validateGridData(): boolean {
    return this.protocolValidationService.validateGridData(this.getAllGridData(), this.gridAPI()!);
  }

  validateSpecificField(colId: ValidationField, rowIndex?: number): boolean {
    const data = rowIndex !== undefined ? [this.getAllGridData()[rowIndex]] : this.getAllGridData();

    return this.protocolValidationService.validateField(colId, data, this.gridAPI()!, false);
  }
}
